import { Analytics } from '../analytics';
import { Logger } from '../logger';

/**
 * Starting screen overlay — shows the splash video during receiver init.
 *
 * Plays `res/splash.mp4` once. After the video ends, shows a circular loader
 * on top of the last frame. The overlay stays visible until `dismissStartingScreen()`
 * is called (by the standby screen once it's fully ready to display).
 *
 * Returns a promise that resolves when the video finishes playing (or after a
 * safety timeout), so the caller can wait for both the video and other resources
 * before transitioning away.
 *
 * **DOM requirement:** `id="starting-screen"` with class `starting-screen`,
 * containing a `<video class="starting-screen__video">` and
 * an `<img class="starting-screen__loader">`.
 */

const LOG_TAG = 'UI_STARTING';

/** CSS class toggled to trigger the fade-out transition. */
const HIDDEN_CLASS = 'screen-overlay--hidden';

/** Time (ms) to wait after adding the hidden class before removing from DOM. */
const FADE_DURATION_MS = 700;

/** Safety timeout (ms) in case the video never fires 'ended'. */
const VIDEO_TIMEOUT_MS = 15_000;

/**
 * Initialises the starting screen.
 *
 * @returns A promise that resolves when the splash video ends (or safety timeout).
 */
export function initStartingScreen(): Promise<void> {
  const el = document.getElementById('starting-screen');

  if (!el) {
    Logger.warn(LOG_TAG, 'Starting screen element not found in DOM');
    return Promise.resolve();
  }

  const video = el.querySelector<HTMLVideoElement>('.starting-screen__video');
  const loader = el.querySelector<HTMLElement>('.starting-screen__loader');

  // Promise that resolves when the video ends (or safety timeout)
  const videoEnded = new Promise<void>((resolve) => {
    if (!video) {
      resolve();
      return;
    }

    let resolved = false;
    const done = (): void => {
      if (resolved) return;
      resolved = true;
      resolve();
    };

    video.addEventListener('ended', () => {
      Logger.debug(LOG_TAG, 'Video ended');
      if (loader) {
        loader.classList.add('starting-screen__loader--visible');
        Logger.debug(LOG_TAG, 'Loader shown');
      }
      done();
    });

    video.addEventListener('error', () => {
      Logger.warn(LOG_TAG, 'Video error — resolving early');
      done();
    });

    setTimeout(() => {
      if (!resolved) {
        Logger.warn(LOG_TAG, 'Video timeout — resolving after safety limit');
        done();
      }
    }, VIDEO_TIMEOUT_MS);
  });

  Analytics.logEvent(Analytics.eventNames.CAST_STARTING_DISPLAYED);
  Logger.info(LOG_TAG, 'Starting screen initialised');

  return videoEnded;
}

/**
 * Fades out and removes the starting screen from the DOM.
 * Called by the standby screen once it's ready to display.
 */
export function dismissStartingScreen(): void {
  const el = document.getElementById('starting-screen');
  if (!el || el.classList.contains(HIDDEN_CLASS)) return;

  const video = el.querySelector<HTMLVideoElement>('.starting-screen__video');

  el.classList.add(HIDDEN_CLASS);
  Logger.info(LOG_TAG, 'Starting screen dismissed');

  setTimeout(() => {
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    el.remove();
    Logger.debug(LOG_TAG, 'Starting screen removed from DOM');
  }, FADE_DURATION_MS);
}
